import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { setCurrentUser as setSharedCurrentUser } from '../services/sharedAuth.service';
import { getToken, setToken, clearToken } from '../services/tokenStore';

/**
 * Spoke backend (etmf-server) — NOT Hub frontend.
 * Login/logout must hit our API first:
 * - /api/auth/login  → creates PKCE+state in server session, then redirects to Hub authorize
 * - /api/auth/logout → clears our session/tokens, then redirects to Hub /api/auth/logout
 * Going straight to hub.dizzaroo.com would skip PKCE/session and break SSO.
 */
const API_BASE = (
  process.env.REACT_APP_API_URL ||
  'https://backend-clinical-intake.dizzaroo.com'
).replace(/\/$/, '');

const SSO_LOGIN_URL = `${API_BASE}/api/auth/login`;
const SSO_LOGOUT_URL = `${API_BASE}/api/auth/logout`;

// JWT lives in memory only (see tokenStore.js) — a hard reload always wipes it and
// forces a full round-trip through Hub SSO (redirectToHubLogin does a top-level
// window.location.href nav, which would otherwise lose the current route). Stash
// where the user actually was in sessionStorage before that nav so login() can
// send them back instead of always landing on '/'. sessionStorage (not localStorage)
// so it doesn't leak across tabs/sessions and self-clears once consumed or the tab closes.
const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirectPath';

function savePostLoginRedirect(path) {
  try {
    if (path) sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
  } catch (e) {
    // sessionStorage unavailable (private browsing, etc.) — login() falls back to '/'
  }
}

function consumePostLoginRedirect() {
  try {
    const saved = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (saved) {
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
      return saved;
    }
  } catch (e) {
    // ignore
  }
  return '/';
}

/** Lowercase role slug from `/auth/me` (string or populated `{ name, level, permissions }`). */
export function userRoleName(role) {
  if (!role) return null;
  if (typeof role === 'string') {
    const s = role.trim().toLowerCase();
    if (/^[a-f\d]{24}$/i.test(s)) return null;
    return s || null;
  }
  if (typeof role === 'object' && role.name != null) return String(role.name).toLowerCase();
  return null;
}

/** System administrator (template locks, etc.). */
export function userRoleIsAdmin(role) {
  if (!role) return false;
  if (userRoleName(role) === 'admin') return true;
  if (typeof role === 'object' && Array.isArray(role.permissions)) {
    return role.permissions.includes('manage:system');
  }
  return false;
}

/**
 * True Hub/App Owner only — narrower than userRoleIsAdmin, which also covers the mapped
 * app-level 'admin' role (settable independently via an app-scoped role attribute without
 * the user actually holding a Hub owner role). Reads the raw `hubRoles` array `/auth/me`
 * returns alongside the mapped `role`.
 */
export function userIsHubOrAppOwner(hubRoles) {
  if (!Array.isArray(hubRoles)) return false;
  return hubRoles.some((r) => ['APP_OWNER', 'HUB_OWNER'].includes(String(r).trim().toUpperCase()));
}

/**
 * Who may edit Study team in Protocol (authors/managers run protocols; reviewers/basic users stay read-only).
 * Aligns with seeded roles: author level 4+, manager 6, admin 9; reviewer level 3 excluded.
 */
export function userCanManageStudyTeam(role) {
  if (!role) return false;
  if (typeof role === 'object') {
    if (typeof role.level === 'number' && role.level >= 4) return true;
    const name = userRoleName(role);
    return !!(name && ['admin', 'author', 'manager'].includes(name));
  }
  const name = userRoleName(role);
  return !!(name && ['admin', 'author', 'manager'].includes(name));
}

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Hub SSO returns to /login?code=… — Login.js owns redeeming that
    // one-time code for the app JWT (via POST /api/auth/exchange) and
    // calling login() itself, so there's nothing to bootstrap from the URL
    // here; a plain mount just checks whatever token is already in memory.
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = getToken();
      if (token) {
        const response = await api.get('/auth/me');
        if (response.data.success) {
          setUser(response.data.user);
          setSharedCurrentUser(response.data.user);
          setIsAuthenticated(true);
          // If we're on the login page and successfully authenticated, redirect back
          // to wherever the user was before the Hub SSO round-trip (or '/' if none saved).
          //
          // Deliberately window.location.pathname, not the `location` from useLocation():
          // login() below is a useCallback keyed only on `navigate` (stable), so its
          // closure — and the checkAuthStatus it calls — freezes at mount, when the page
          // was still on '/login?code=…'. By the time this async /auth/me call resolves,
          // login()'s own earlier `navigate(consumePostLoginRedirect(), …)` has already
          // moved the app to the real destination — but the closed-over `location.pathname`
          // here would still read stale as '/login', firing a second, now-wrong redirect
          // (consumePostLoginRedirect() already popped its one-time value, so this second
          // call fell back to '/' — the app briefly showed the right tab, then bounced to
          // Home). The live window.location.pathname reflects the real current URL instead.
          if (window.location.pathname === '/login') {
            navigate(consumePostLoginRedirect(), { replace: true });
          }
        } else {
          clearLocalAuth();
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      clearLocalAuth();
    } finally {
      setLoading(false);
    }
  };

  const login = useCallback((token) => {
    setToken(token);
    setIsAuthenticated(true);
    setLoading(false);
    navigate(consumePostLoginRedirect(), { replace: true });
    checkAuthStatus();
  }, [navigate]);

  const clearLocalAuth = () => {
    clearToken();
    setUser(null);
    setSharedCurrentUser(null);
    setIsAuthenticated(false);
  };

  const redirectToHubLogin = useCallback(() => {
    savePostLoginRedirect(window.location.pathname + window.location.search);
    window.location.href = SSO_LOGIN_URL;
  }, []);

  const logout = useCallback(async () => {
    const token = getToken();
    clearLocalAuth();

    // POST with Bearer so backend can revoke/clear Hub tokens even without session cookie
    try {
      const response = await fetch(SSO_LOGOUT_URL, {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = await response.json().catch(() => ({}));
      if (data.hubLogoutUrl) {
        window.location.href = data.hubLogoutUrl;
        return;
      }
    } catch (err) {
      console.warn('Logout API call failed, falling back to GET logout:', err);
    }

    window.location.href = SSO_LOGOUT_URL;
  }, []);

  const isAdmin = useMemo(() => userRoleIsAdmin(user?.role), [user]);
  const isHubOwner = useMemo(() => userIsHubOrAppOwner(user?.hubRoles), [user]);
  const canManageStudyTeam = useMemo(() => userCanManageStudyTeam(user?.role), [user]);

  const value = {
    user,
    setUser,
    isAdmin,
    isHubOwner,
    canManageStudyTeam,
    isAuthenticated,
    loading,
    login,
    logout,
    redirectToHubLogin,
    checkAuthStatus,
    consumePostLoginRedirect,
  };
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 